import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { Box, Chip, FormControlLabel, Stack, Switch, Tab, Tabs, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import { FilterChipBarV2, InputV2, TableV2 } from '../../../components/v2';
import { renderStatus } from '../../../components/v2/cell-renderers/status';
import { OPTION_CODE_ALLOWLIST, optionModulesFor } from '../../../constants/optionCodes';
import { isAdmin } from '../../../constants/role';
import {
  apiErrorMessage,
  fetchMyOptionCodes,
  fetchOptionValues,
  setOptionAtivo,
} from '../../../helpers/optionApi';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as actionsToast from '../../../store/toast/actions';
import Formulario from './Formulario';

const tabSx = {
  minHeight: 40,
  borderBottom: 1,
  borderColor: 'divider',
  '& .MuiTab-root': {
    minHeight: 40,
    textTransform: 'none',
    fontWeight: 600,
  },
};

const QUERY_CODIGO = 'optionCodigo';
const QUERY_OPCAO = 'opcaoId';

const readParam = (search, name) => {
  const raw = new URLSearchParams(search || '').get(name);
  return raw && String(raw).trim() ? String(raw).trim() : null;
};

const OpcoesV2 = ({ authData, history, location, messageSuccess, messageError }) => {
  const admin = isAdmin(authData?.role);
  const reportError = useRef(messageError);
  reportError.current = messageError;
  const [grantedCodes, setGrantedCodes] = useState([]);
  const [accessReady, setAccessReady] = useState(admin);
  const [codigo, setCodigo] = useState(() => readParam(location.search, QUERY_CODIGO) || '');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(true);
  const [busca, setBusca] = useState('');
  const [reload, setReload] = useState(0);

  const opcaoId = readParam(location.search, QUERY_OPCAO);
  const open = Boolean(opcaoId);

  const modules = useMemo(() => {
    const source = admin ? OPTION_CODE_ALLOWLIST.map((item) => item.codigo) : grantedCodes;
    return optionModulesFor(source);
  }, [admin, grantedCodes]);

  const codes = useMemo(
    () => modules.flatMap((mod) => mod.groups.flatMap((group) => group.codes.map((item) => item.codigo))),
    [modules],
  );

  const activeCodigo = codes.includes(codigo) ? codigo : (codes[0] || '');
  const activeModule = modules.find((mod) => (
    mod.groups.some((group) => group.codes.some((item) => item.codigo === activeCodigo))
  )) || modules[0];

  const replaceSearch = useCallback((mutate) => {
    const params = new URLSearchParams(location.search);
    mutate(params);
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    });
  }, [history, location.pathname, location.search]);

  const setOpcaoInUrl = useCallback((value) => {
    replaceSearch((params) => {
      if (activeCodigo) params.set(QUERY_CODIGO, activeCodigo);
      if (!value) params.delete(QUERY_OPCAO);
      else params.set(QUERY_OPCAO, String(value));
    });
  }, [activeCodigo, replaceSearch]);

  useEffect(() => {
    if (admin) {
      setAccessReady(true);
      return undefined;
    }
    let cancel = false;
    fetchMyOptionCodes()
      .then((list) => {
        if (!cancel) setGrantedCodes(list);
      })
      .catch((error) => {
        if (!cancel) {
          reportError.current({ message: apiErrorMessage(error, 'Não foi possível carregar suas permissões.') });
        }
      })
      .finally(() => {
        if (!cancel) setAccessReady(true);
      });
    return () => {
      cancel = true;
    };
  }, [admin]);

  useEffect(() => {
    if (!accessReady || !activeCodigo) return undefined;
    let cancel = false;
    setLoading(true);
    fetchOptionValues(activeCodigo)
      .then((list) => {
        if (!cancel) setRows(list);
      })
      .catch((error) => {
        if (!cancel) {
          setRows([]);
          reportError.current({ message: apiErrorMessage(error, 'Não foi possível carregar os valores.') });
        }
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [accessReady, activeCodigo, reload]);

  const selectCodigo = (next) => {
    setBusca('');
    setCodigo(next);
    replaceSearch((params) => {
      params.set(QUERY_CODIGO, next);
      params.delete(QUERY_OPCAO);
    });
  };

  const selectModule = (moduleId) => {
    const mod = modules.find((item) => item.id === moduleId);
    const first = mod?.groups?.[0]?.codes?.[0];
    if (first) selectCodigo(first.codigo);
  };

  const buscaNormalizada = busca.trim().toLowerCase();
  const visibleRows = rows.filter((row) => {
    if (!showInactive && row.ativo === false) return false;
    if (!buscaNormalizada) return true;
    const value = String(row.value || '').toLowerCase();
    const description = String(row.description || '').toLowerCase();
    return value.includes(buscaNormalizada) || description.includes(buscaNormalizada);
  });
  const editingRow = rows.find((row) => String(row.id) === String(opcaoId)) || null;

  const toggleAtivo = async (row) => {
    const next = row.ativo === false;
    try {
      await setOptionAtivo(row.id, activeCodigo, next);
      messageSuccess({ message: next ? 'Valor reativado.' : 'Valor inativado.' });
      setReload((value) => value + 1);
    } catch (error) {
      messageError({ message: apiErrorMessage(error, 'Não foi possível alterar a situação.') });
    }
  };

  if (!accessReady) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">Carregando permissões…</Typography>
      </Box>
    );
  }

  if (!codes.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h4">Opções</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Você não tem permissão para gerenciar opções.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={3}>
        <Typography variant="h4">Opções</Typography>

        <Stack spacing={1.5}>
        <Stack spacing={0}>
          {modules.length > 1 ? (
            <Tabs
              value={activeModule?.id || false}
              onChange={(_, next) => selectModule(next)}
              variant="scrollable"
              scrollButtons="auto"
              sx={tabSx}
            >
              {modules.map((mod) => (
                <Tab key={mod.id} value={mod.id} label={mod.label} />
              ))}
            </Tabs>
          ) : null}

          {activeModule && activeModule.groups.length > 1 ? (
            <Box sx={{ pt: 1.5, '& > *': { mb: '0 !important' } }}>
              <FilterChipBarV2
                searchLabel=""
                showCount={false}
                chipGroupsLayout="row"
                chipGroups={activeModule.groups.map((group) => ({
                  label: group.label,
                  chips: group.codes.map((item) => ({
                    id: item.codigo,
                    label: item.label,
                    selected: item.codigo === activeCodigo,
                    onClick: () => selectCodigo(item.codigo),
                  })),
                }))}
              />
            </Box>
          ) : null}

          {activeModule && activeModule.groups.length === 1 && activeModule.groups[0].codes.length > 1 ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 1.5 }}>
              {activeModule.groups[0].codes.map((item) => {
                const selected = item.codigo === activeCodigo;
                return (
                  <Chip
                    key={item.codigo}
                    label={item.label}
                    clickable
                    size="small"
                    variant={selected ? 'filled' : 'outlined'}
                    color={selected ? 'primary' : 'default'}
                    onClick={() => selectCodigo(item.codigo)}
                  />
                );
              })}
            </Stack>
          ) : null}
        </Stack>

        <TableV2
          rows={visibleRows}
          loading={loading}
          rowHeight={52}
          emptyMessage={buscaNormalizada ? 'Nenhum valor para essa busca.' : 'Nenhum valor neste código.'}
          headerStartActions={[{
            key: 'busca',
            node: (
              <Box sx={{ width: { xs: '100%', sm: 480 } }}>
                <InputV2
                  name="busca"
                  value={busca}
                  onChange={(event) => setBusca(event.target.value)}
                  placeholder="Busque por valor e descrição..."
                  hideHelperText
                  showInputGroup={false}
                />
              </Box>
            ),
          }]}
          headerActions={[
            {
              key: 'inativos',
              node: (
                <FormControlLabel
                  control={(
                    <Switch
                      checked={showInactive}
                      onChange={(event) => setShowInactive(event.target.checked)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'grey.300',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'grey.600',
                          opacity: 1,
                        },
                      }}
                    />
                  )}
                  label="Mostrar inativos"
                  sx={{
                    mr: 0.5,
                    '& .MuiFormControlLabel-label': {
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                    },
                  }}
                />
              ),
            },
            {
              label: 'Novo valor',
              onClick: () => setOpcaoInUrl('novo'),
              startIcon: <AddIcon />,
              variant: 'contained',
            },
          ]}
              onEdit={(rowId) => setOpcaoInUrl(rowId)}
              actions={[
                {
                  key: 'inativar',
                  title: 'Inativar',
                  icon: <NotInterestedIcon fontSize="small" />,
                  show: (row) => row.ativo !== false,
                  onClick: (_rowId, row) => toggleAtivo(row),
                },
                {
                  key: 'reativar',
                  title: 'Reativar',
                  icon: <CheckCircleOutlineIcon fontSize="small" />,
                  show: (row) => row.ativo === false,
                  onClick: (_rowId, row) => toggleAtivo(row),
                },
              ]}
              columns={[
                { field: 'value', headerName: 'Valor', flex: 1, minWidth: 180 },
                {
                  field: 'description',
                  headerName: 'Descrição',
                  flex: 1,
                  minWidth: 160,
                  valueGetter: (_, row) => row.description || '—',
                },
                {
                  field: 'ativo',
                  headerName: 'Situação',
                  width: 130,
                  renderCell: (params) => renderStatus({
                    value: params.row.ativo === false ? 'EXPIRADA' : 'ATIVO',
                    label: params.row.ativo === false ? 'Inativo' : 'Ativo',
                  }),
                },
              ]}
            />
        </Stack>

        <Formulario
          open={open}
          id={opcaoId}
          codigo={activeCodigo}
          row={editingRow}
          onClose={() => setOpcaoInUrl(null)}
          onSaved={(message) => {
            messageSuccess({ message });
            setReload((value) => value + 1);
          }}
          onError={(error) => {
            messageError({ message: apiErrorMessage(error, 'Não foi possível salvar o valor.') });
          }}
        />
      </Stack>
    </Box>
  );
};

OpcoesV2.propTypes = {
  authData: PropTypes.object.isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  messageSuccess: PropTypes.func.isRequired,
  messageError: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ ...actionsToast }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(OpcoesV2));
